import React, { useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { reportApi } from '../../../services/reportService';
import { StatTile, PersonAvatar } from '../../../components/ui';
import { tokens } from '../../../theme';
import { formatDateLong } from '../../../utils/format';
import {
  SectionCard,
  CHART_COLORS,
  formatPLN,
  formatNumber,
  formatPct,
  useAsyncData,
  StatsLoader,
  EmptyHint,
  RankList,
  StatTileGrid,
  StatsRow,
  MetricToggle,
} from './statsShared';
import { GroupedBarChart, DonutChart } from './ChartKit';

interface Props {
  salonId: string;
}

const today = () => new Date().toISOString().split('T')[0];

const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

export const DayTab: React.FC<Props> = ({ salonId }) => {
  const [date, setDate] = useState(today());
  const [metric, setMetric] = useState<'revenue' | 'bookings'>('revenue');
  const { data, loading, error } = useAsyncData(() => reportApi.getDayStats(salonId, date), [salonId, date]);

  const maxServiceRevenue = Math.max(1, ...(data?.topServices ?? []).map((s) => s.revenue));

  // Godziny od pierwszej do ostatniej z ruchem — bez przeskokow na osi.
  const active = (data?.hourly ?? []).filter((h) => h.revenue > 0 || h.bookings > 0).map((h) => h.hour);
  const hourly = active.length
    ? (data?.hourly ?? [])
        .filter((h) => h.hour >= Math.min(...active) && h.hour <= Math.max(...active))
        .map((h) => ({ label: `${String(h.hour).padStart(2, '0')}:00`, revenue: h.revenue, bookings: h.bookings }))
    : [];

  return (
    <Stack gap={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'flex-end' }} gap={{ xs: 1, sm: 2 }} sx={{ mb: 1 }}>
        <TextField
          type="date"
          size="small"
          label="Dzień"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          inputProps={{ max: today() }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 190 } }}
        />
        {data && (
          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: tokens.inkSoft, pb: { sm: 1 }, '&::first-letter': { textTransform: 'uppercase' } }}>
            {formatDateLong(data.date)}
          </Typography>
        )}
      </Stack>

      <StatsLoader loading={loading} error={error} hasData={!!data}>
        {data && (
          <Stack gap={2}>
            <StatTileGrid>
              <StatTile label="Przychód dnia" value={formatPLN(data.revenue)} />
              <StatTile label="Wszystkie wizyty" value={formatNumber(data.bookings.total)} />
              <StatTile label="Zakończone" value={formatNumber(data.bookings.completed)} />
              <StatTile label="Anulowane" value={formatNumber(data.bookings.cancelled)} />
            </StatTileGrid>

            <StatsRow template="2fr 1fr">
              <SectionCard
                title="Rozkład godzinowy"
                subtitle={metric === 'revenue' ? 'Przychód w kolejnych godzinach' : 'Liczba wizyt w kolejnych godzinach'}
                action={hourly.length ? <MetricToggle value={metric} onChange={setMetric} /> : undefined}
              >
                {hourly.length ? (
                  <GroupedBarChart
                    data={hourly as unknown as Record<string, unknown>[]}
                    xKey="label"
                    bars={
                      metric === 'revenue'
                        ? [{ key: 'revenue', name: 'Przychód', color: CHART_COLORS[0], currency: true }]
                        : [{ key: 'bookings', name: 'Wizyty', color: CHART_COLORS[0] }]
                    }
                  />
                ) : (
                  <EmptyHint description="Wybierz inny dzień, żeby zobaczyć rozkład.">Brak wizyt i sprzedaży w tym dniu</EmptyHint>
                )}
              </SectionCard>
              <SectionCard title="Metody płatności">
                {(() => {
                  const p = data.paymentBreakdown;
                  const donut = [
                    { name: 'Karta', value: p.card, color: CHART_COLORS[0] },
                    { name: 'Gotówka', value: p.cash, color: CHART_COLORS[1] },
                    { name: 'BLIK', value: p.blik, color: CHART_COLORS[2] },
                    { name: 'Przelew', value: p.transfer, color: CHART_COLORS[3] },
                  ];
                  return donut.some((d) => d.value > 0) ? <DonutChart data={donut} /> : <EmptyHint>Brak wpłat w tym dniu</EmptyHint>;
                })()}
              </SectionCard>
            </StatsRow>

            <StatsRow template="7fr 5fr">
              <SectionCard title="Pracownicy tego dnia" padding={data.staffPerformance.length ? 'none' : 'normal'}>
                {data.staffPerformance.length ? (
                  <TableContainer>
                    <Table sx={{ minWidth: 480 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Pracownik</TableCell>
                          <TableCell align="right">Wizyty</TableCell>
                          <TableCell align="right">Przychód</TableCell>
                          <TableCell align="right">Obłożenie</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.staffPerformance.map((s) => (
                          <TableRow key={s.id} hover>
                            <TableCell>
                              <Stack direction="row" alignItems="center" gap={1.25}>
                                <PersonAvatar firstName={s.firstName} lastName={s.lastName} seed={s.id} size={32} />
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                  {s.firstName} {s.lastName}
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={NUM}>{formatNumber(s.bookings)}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, fontWeight: 700 }}>
                              {formatPLN(s.revenue)}
                            </TableCell>
                            <TableCell align="right" sx={NUM}>{formatPct(s.occupancy)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <EmptyHint>Nikt z zespołu nie miał wizyt w tym dniu</EmptyHint>
                )}
              </SectionCard>
              <SectionCard title="Najlepsze usługi dnia">
                {data.topServices.length ? (
                  <RankList
                    items={data.topServices.slice(0, 6).map((s) => ({
                      id: s.id,
                      label: s.name,
                      sub: `${formatNumber(s.count)}×`,
                      value: formatPLN(s.revenue),
                      ratio: s.revenue / maxServiceRevenue,
                    }))}
                  />
                ) : (
                  <EmptyHint>Brak usług w tym dniu</EmptyHint>
                )}
              </SectionCard>
            </StatsRow>
          </Stack>
        )}
      </StatsLoader>
    </Stack>
  );
};
